'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, AlertCircle, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const ADMIN_CREDENTIALS = { email: 'lider001@crm.com', password: '$0510$0056' };

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const STORAGE_KEY_ATTEMPTS = 'admin_login_attempts';
const STORAGE_KEY_LOCKOUT = 'admin_login_lockout_until';

function getRemainingLockoutTime(): number {
  try {
    const lockoutUntil = parseInt(localStorage.getItem(STORAGE_KEY_LOCKOUT) || '0', 10);
    const remaining = lockoutUntil - Date.now();
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
}

function getFailedAttempts(): number {
  try {
    return parseInt(localStorage.getItem(STORAGE_KEY_ATTEMPTS) || '0', 10);
  } catch {
    return 0;
  }
}

function recordFailedAttempt(): number {
  try {
    const attempts = getFailedAttempts() + 1;
    localStorage.setItem(STORAGE_KEY_ATTEMPTS, String(attempts));
    if (attempts >= MAX_ATTEMPTS) {
      localStorage.setItem(STORAGE_KEY_LOCKOUT, String(Date.now() + LOCKOUT_DURATION_MS));
    }
    return attempts;
  } catch {
    return 1;
  }
}

function clearLoginAttempts(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_ATTEMPTS);
    localStorage.removeItem(STORAGE_KEY_LOCKOUT);
  } catch {
    // ignore
  }
}

function formatRemainingTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export default function AdminLoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [remainingLockout, setRemainingLockout] = useState(0);
  const [failedAttempts, setFailedAttempts] = useState(0);

  // Initialize state from localStorage after mount (avoid hydration mismatch)
  useEffect(() => {
    const remaining = getRemainingLockoutTime();
    const attempts = getFailedAttempts();
    setRemainingLockout(remaining);
    setFailedAttempts(attempts);
  }, []);

  // Countdown timer during lockout
  useEffect(() => {
    if (remainingLockout <= 0) return;
    const interval = setInterval(() => {
      const remaining = getRemainingLockoutTime();
      setRemainingLockout(remaining);
      if (remaining <= 0) {
        clearLoginAttempts();
        setFailedAttempts(0);
        setAuthError('');
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [remainingLockout]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    // Check lockout before attempting
    const lockoutRemaining = getRemainingLockoutTime();
    if (lockoutRemaining > 0) {
      setRemainingLockout(lockoutRemaining);
      return;
    }

    setAuthError('');
    setIsLoading(true);

    await new Promise((r) => setTimeout(r, 1000));

    if (data.email === ADMIN_CREDENTIALS.email && data.password === ADMIN_CREDENTIALS.password) {
      clearLoginAttempts();
      setFailedAttempts(0);
      toast.success('Bienvenido, Administrador', {
        description: 'Acceso concedido. Redirigiendo...',
      });
      setTimeout(() => router.push('/case-management'), 800);
    } else {
      const attempts = recordFailedAttempt();
      setFailedAttempts(attempts);
      const newLockout = getRemainingLockoutTime();

      if (newLockout > 0) {
        setRemainingLockout(newLockout);
        setAuthError('');
      } else {
        const attemptsLeft = MAX_ATTEMPTS - attempts;
        if (attemptsLeft > 0) {
          setAuthError(
            `Credenciales incorrectas. Verifique su correo y contraseña. (${attemptsLeft} intento${attemptsLeft !== 1 ? 's' : ''} restante${attemptsLeft !== 1 ? 's' : ''})`
          );
        } else {
          setAuthError('');
        }
      }
      setIsLoading(false);
    }
  };

  const isLocked = remainingLockout > 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      {/* Lockout banner */}
      {isLocked && (
        <div className="flex gap-2.5 bg-orange-50 border border-orange-200 rounded-lg p-3.5">
          <ShieldAlert size={16} className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-orange-800 leading-snug">
              Cuenta bloqueada temporalmente
            </p>
            <p className="text-xs text-orange-700 mt-0.5">
              Demasiados intentos fallidos. Intente nuevamente en{' '}
              <span className="font-bold">{formatRemainingTime(remainingLockout)}</span>.
            </p>
          </div>
        </div>
      )}

      {/* Auth error */}
      {!isLocked && authError && (
        <div className="flex gap-2.5 bg-red-50 border border-red-200 rounded-lg p-3.5 fade-in">
          <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 leading-snug">{authError}</p>
        </div>
      )}

      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-semibold text-foreground mb-1.5"
        >
          Correo electrónico
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="usuario@dominio.com"
          disabled={isLocked}
          className={`w-full px-3.5 py-2.5 text-sm bg-card border rounded-lg outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/20 disabled:opacity-50 disabled:cursor-not-allowed ${
            errors.email ? 'border-red-400' : 'border-input'
          }`}
          {...register('email', {
            required: 'El correo electrónico es requerido.',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Ingrese un correo electrónico válido.',
            },
          })}
        />
        {errors.email && (
          <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
            <AlertCircle size={12} />
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Password */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm font-semibold text-foreground mb-1.5"
        >
          Contraseña
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••••"
            disabled={isLocked}
            className={`w-full px-3.5 py-2.5 pr-10 text-sm bg-card border rounded-lg outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/20 disabled:opacity-50 disabled:cursor-not-allowed ${
              errors.password ? 'border-red-400' : 'border-input'
            }`}
            {...register('password', {
              required: 'La contraseña es requerida.',
              minLength: {
                value: 6,
                message: 'La contraseña debe tener al menos 6 caracteres.',
              },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLocked}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
            <AlertCircle size={12} />
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <input
          id="remember"
          type="checkbox"
          disabled={isLocked}
          className="w-4 h-4 rounded border-input accent-accent cursor-pointer disabled:cursor-not-allowed"
          {...register('remember')}
        />
        <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
          Mantener sesión iniciada
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading || isLocked}
        className="w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 active:scale-95 text-accent-foreground font-semibold text-sm py-3 rounded-lg transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Verificando credenciales...
          </>
        ) : isLocked ? (
          <>
            <ShieldAlert size={16} />
            Bloqueado — {formatRemainingTime(remainingLockout)}
          </>
        ) : (
          'Iniciar sesión'
        )}
      </button>
    </form>
  );
}